import React, { useCallback, useEffect, useState } from "react";
import { errorToast, successToast } from "@idwal/idwal-react-components";
import { textGenTitles, titleToKeyMap } from "../config/TextGenerationTitles";
import { fetchInspectionData } from "../store/thunks/inspectionThunks";
import { fetchAiSummaries, updateAiSummaries } from "../store/thunks/aiSummaryThunks";
import { useAppDispatch, useAppSelector } from "../hooks/storeHooks";
import { useParams } from "react-router-dom";
import { selectInspection } from "../store/selectors/inspectionSelectors";
import { selectAiSummaries, selectAiSummariesLoading, selectAnyAiSummariesUpdateLoading, selectAiSummariesUpdateError, selectAiSummariesUpdateSuccess, selectAiSummariesUpdateLoadingForSection } from "../store/selectors/aiSummarySelectors";
import { AiSummaryHeader } from "../components/AISummary/AiSummaryHeader";
import { AiSummarySection } from "../components/AISummary/AiSummarySection";
import { AiSummaryHeaderSkeleton, AiSummarySectionsSkeleton } from "../components/AISummary/AiSummarySkeletons";
import { SaveAllSection } from "../components/AISummary/SaveAllSection";
import { NoAiSummaries } from "../components/AISummary/NoAiSummaries";

export default function AiSummary() {
    const dispatch = useAppDispatch();
    const { inspectionId } = useParams();
    const { inspection, loading: inspectionLoading } = useAppSelector(selectInspection);
    
    // AI Summary state
    const aiSummaries = useAppSelector(selectAiSummaries);
    const loadingAiSummaries = useAppSelector(selectAiSummariesLoading);
    const isAnyUpdateLoading = useAppSelector(selectAnyAiSummariesUpdateLoading);
    const isSaveAllLoading = useAppSelector(selectAiSummariesUpdateLoadingForSection('all'));
    const errorAiSummariesUpdate = useAppSelector(selectAiSummariesUpdateError);
    const successAiSummariesUpdate = useAppSelector(selectAiSummariesUpdateSuccess);

    const [editableTexts, setEditableTexts] = useState<string[]>(Array(textGenTitles.length).fill(''));
    const [originalValues, setOriginalValues] = useState<string[]>(Array(textGenTitles.length).fill(''));
    const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0])); // First section expanded by default

    useEffect(() => {
        if (inspectionId && !inspection) {
            dispatch(fetchInspectionData(inspectionId));
        }
        if (inspectionId) {
            dispatch(fetchAiSummaries(inspectionId));
        }
    }, [inspectionId, inspection, dispatch]);

    useEffect(() => {
        if (aiSummaries && Array.isArray(aiSummaries) && aiSummaries.length > 0) {
            const newValues = textGenTitles.map(title => {
                const key = titleToKeyMap[title];
                const summaryItem = aiSummaries.find(item => item.sectionId === key);
                return summaryItem ? summaryItem.augmentedText : '';
            });
            setEditableTexts(newValues);
            setOriginalValues([...newValues]); // Store original editableTexts for comparison
        }
    }, [aiSummaries]);

    // Handle error toasts for AI summaries updates
    useEffect(() => {
        if (errorAiSummariesUpdate) {
            errorToast("Failed to update AI summaries. Please try again.");
        }
    }, [errorAiSummariesUpdate]);

    // Handle success toasts - triggers every time the counter changes
    useEffect(() => {
        if (successAiSummariesUpdate > 0) {
            successToast("Updated summary");
        }
    }, [successAiSummariesUpdate]);

    const updateValue = (index: number, value: string) => {
        const newValues = [...editableTexts];
        newValues[index] = value;
        setEditableTexts(newValues);
    };

    const toggleSection = (index: number) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedSections(newExpanded);
    };

    const onSaveSection = useCallback((index: number) => {
        if (inspectionId && inspection?.inspectionNumber) {
            const inspectionNumber = inspection.inspectionNumber;
            const title = textGenTitles[index];
            const sectionKey = titleToKeyMap[title];
            const titleToValueMap = {
                [sectionKey]: editableTexts[index]
            };

            dispatch(updateAiSummaries(inspectionNumber, titleToValueMap, String(sectionKey)));
        }
    }, [dispatch, inspectionId, inspection, editableTexts]);

    const onSaveAll = useCallback(() => {
        if (inspectionId && inspection?.inspectionNumber) {
            const inspectionNumber = inspection.inspectionNumber;
            
            // Build a single object with all sections
            const titleToValueMap: Record<string, string> = {};
            textGenTitles.forEach((title, index) => {
                const sectionKey = titleToKeyMap[title];
                titleToValueMap[sectionKey] = editableTexts[index];
            });

            // Save all sections in a single API call
            dispatch(updateAiSummaries(inspectionNumber, titleToValueMap, 'all'));
        }
    }, [dispatch, inspectionId, inspection, editableTexts]);

    const getOriginalText = (index: number): string => {
        const title = textGenTitles[index];
        const key = titleToKeyMap[title];
        const summaryItem = aiSummaries.find(item => item.sectionId === key);
        return summaryItem?.originalText || '';
    };

    const hasBeenEdited = (index: number): boolean => {
        return editableTexts[index] !== originalValues[index];
    };

    const handleExpandAll = () => {
        setExpandedSections(new Set(Array.from({length: textGenTitles.length}, (_, i) => i)));
    };

    const handleCollapseAll = () => {
        setExpandedSections(new Set());
    };

    // Show skeleton loading while data is being fetched
    if (inspectionLoading) {
        return (
            <div className="w-full">
                <AiSummaryHeaderSkeleton />
                <AiSummarySectionsSkeleton />
            </div>
        );
    }

    return (
        <div className="w-full">
            {/* Header Section - controlled by inspectionLoading */}
            {inspectionLoading ? (
                <AiSummaryHeaderSkeleton />
            ) : (
                <AiSummaryHeader
                    totalSections={textGenTitles.length}
                    onExpandAll={handleExpandAll}
                    onCollapseAll={handleCollapseAll}
                />
            )}

            {/* Sections - controlled by loadingAiSummaries */}
            {loadingAiSummaries && (!aiSummaries || aiSummaries.length === 0) ? (
                <AiSummarySectionsSkeleton />
            ) : (
                (!aiSummaries || !Array.isArray(aiSummaries) || aiSummaries.length === 0) ? (
                    <NoAiSummaries />
                ) : (
                    <>
                        {/* Summary Sections */}
                        <div className="grid">
                            {editableTexts.map((value, index) => (
                                <AiSummarySection
                                    key={index}
                                    title={textGenTitles[index]}
                                    index={index}
                                    originalText={getOriginalText(index)}
                                    editableText={value}
                                    sectionKey={String(titleToKeyMap[textGenTitles[index]])}
                                    isExpanded={expandedSections.has(index)}
                                    hasBeenEdited={hasBeenEdited(index)}
                                    onToggle={() => toggleSection(index)}
                                    onTextChange={(newValue) => updateValue(index, newValue)}
                                    onSave={() => onSaveSection(index)}
                                />
                            ))}
                        </div>

                        {/* Global Actions */}
                        <SaveAllSection
                            isLoading={isSaveAllLoading}
                            onSaveAll={onSaveAll}
                        />
                    </>
                )
            )}
        </div>
    )
}
